import { BLUE_600, GRAY_800, WHITE } from 'constants/styling/theme'
import { IconButton, Slider, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'

import { MUITooltip } from 'components/common/MUITooltip'
import ReplayIcon from '@mui/icons-material/Replay'
import Stack from '@mui/material/Stack'
import { useMaskDrawer } from '../MaskDrawer/MaskDrawer.context'
import { useTranslation } from 'react-i18next'

/**
 * @component
 *
 * @example
 * <PhotoStudioPreviewToolbar />
 */
export const PhotoStudioPreviewToolbar: React.FC = () => {
  const { t } = useTranslation(['photo_studio_editing'])

  const { brushSize, updateBrushSize, clearMask, hasMaskDrawn, maskUrl } = useMaskDrawer()

  // Track mask state reactively - update whenever maskUrl changes (mask drawn or cleared)
  // Check maskUrl directly first (simpler and more reliable), then fall back to hasMaskDrawn for canvas check
  const [hasMaskToClear, setHasMaskToClear] = useState(() => maskUrl !== null || hasMaskDrawn())

  // Update hasMaskToClear whenever maskUrl changes (mask drawn or cleared)
  // Use requestAnimationFrame to ensure canvas state is checked after DOM updates
  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      // Check maskUrl first - if it exists, there's definitely a mask
      // This ensures the button is enabled after the first mask, not just after the second
      setHasMaskToClear(maskUrl !== null || hasMaskDrawn())
    })
    return () => cancelAnimationFrame(frameId)
  }, [maskUrl, hasMaskDrawn])

  // Handle clear mask and update state
  const handleClearMask = () => {
    clearMask()
    // Update state after clearing using requestAnimationFrame to ensure canvas is updated
    // After clearing, maskUrl will be null, so just check hasMaskDrawn
    requestAnimationFrame(() => {
      setHasMaskToClear(hasMaskDrawn())
    })
  }

  return (
    <Stack alignItems={'center'} display={'flex'} flexDirection={'row'}>

      <Typography variant='text-xs' fontWeight={500} color={WHITE} sx={{ paddingX: '2.4rem', whiteSpace: 'nowrap' }}>
        {t('brush_size')}
      </Typography>

      <Slider
        aria-label="magic eraser brush size"
        value={brushSize}
        onChange={(e, val) => updateBrushSize(val as number)}
        min={1}
        max={80}
        step={1}
        style={{ width: 180, margin: '0 auto' }}
        sx={{
          color: BLUE_600,
          height: 8,
          '& .MuiSlider-track': {
            border: 'none',
            backgroundColor: BLUE_600,
          },
          '& .MuiSlider-rail': {
            backgroundColor: GRAY_800,
            opacity: 1,
          },
          '& .MuiSlider-thumb': {
            width: 12,
            height: 12,
            backgroundColor: WHITE,
            border: '0rem solid currentColor',
            position: 'relative',
            '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
              boxShadow: 'inherit',
            },
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              backgroundColor: BLUE_600,
            },
          },
        }}
      />

      <MUITooltip content={hasMaskToClear ? t('clear_mask_tooltip') : t('clear_mask_disabled_tooltip')} placement="bottom">
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.8}
          onClick={hasMaskToClear ? handleClearMask : undefined}
          sx={{
            paddingX: '2.4rem',
            cursor: hasMaskToClear ? 'pointer' : 'default',
            pointerEvents: hasMaskToClear ? 'auto' : 'none',
            opacity: hasMaskToClear ? 1 : 0.5,
            '&:hover': {
              opacity: hasMaskToClear ? 0.8 : 0.5,
            },
          }}
        >
          <IconButton
            disabled={!hasMaskToClear}
            sx={{
              color: hasMaskToClear ? WHITE : GRAY_800,
              borderColor: hasMaskToClear ? WHITE : GRAY_800,
              padding: '0.4rem',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              verticalAlign: 'middle',
              pointerEvents: 'none', // Disable pointer events on icon button since parent handles clicks
              '&.Mui-disabled': {
                color: GRAY_800,
                borderColor: GRAY_800,
              },
            }}
            size="small"
          >
            <ReplayIcon fontSize="small" sx={{ display: 'block' }} />
          </IconButton>

          <Typography
            variant='text-xs'
            fontWeight={500}
            color={hasMaskToClear ? WHITE : GRAY_800}
            sx={{
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              margin: 0,
              padding: 0,
              pointerEvents: 'none', // Disable pointer events on text since parent handles clicks
            }}
          >
            {t('clear_masks')}
          </Typography>

        </Stack>
      </MUITooltip>

    </Stack >
  )
}
